import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { CheckCircle, Shield, Car, Bike, Palette, Sparkles } from "lucide-react";
import heroPPF from "@/assets/hero-ppf.jpg";
import ferrariFullBody from "@/assets/ferrari-sf90-full-body.jpg";
import ferrariTrackPack from "@/assets/ferrari-sf90-track-pack.jpg";
import ferrariFullFront from "@/assets/ferrari-sf90-full-front.jpg";
import { Card, CardContent } from "@/components/ui/card";

const PaintProtectionFilm = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Paint Protection Film Las Vegas (Clear Bra) | XPEL Official</title>
        <meta name="description" content="Protect your vehicle with XPEL Ultimate Plus PPF in Las Vegas. Self-healing clear bra technology guards against rock chips, scratches & UV damage. Free quote." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroPPF} 
            alt="XPEL Paint Protection Film Las Vegas - PPF Installation" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/75" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Shield className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Paint Protection Film
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              XPEL Ultimate Plus self-healing film technology - The ultimate defense for your vehicle's finish
            </p>
            <Button asChild size="lg" variant="premium">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              Why Paint Protection Film?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Self-Healing Technology</h3>
                  <p className="text-muted-foreground">
                    Minor scratches and swirl marks disappear with heat, keeping your vehicle looking pristine
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Rock Chip Protection</h3>
                  <p className="text-muted-foreground">
                    8-mil thick film absorbs impact from road debris, preventing paint damage
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV & Chemical Resistant</h3>
                  <p className="text-muted-foreground">
                    Protects against sun damage, acid rain, bird droppings, and environmental contaminants
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Maintains Resale Value</h3>
                  <p className="text-muted-foreground">
                    Preserves factory paint condition, maximizing your vehicle's long-term value
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Invisible Protection</h3>
                  <p className="text-muted-foreground">
                    Crystal-clear finish maintains your vehicle's original appearance
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">10-Year Warranty</h3>
                  <p className="text-muted-foreground">
                    Manufacturer-backed warranty for peace of mind and long-term protection
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              See XPEL PPF In Action
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Watch how our paint protection film defends against rock chips, scratches, and more
            </p>
            <div className="relative aspect-video rounded-lg overflow-hidden border border-border">
              <video
                className="w-full h-full object-cover"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source 
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/videos/1763209886542.mp4`}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* PPF Packages Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* PPF Packages Visual Comparison */}
            <div className="bg-[#1B1A1A] -mx-4 px-4 py-16 rounded-2xl">
              <h3 className="text-3xl font-heading font-bold mb-12 text-center text-white">
                Our Paint Protection Film Packages
              </h3>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* Full Body Protection */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariFullBody}
                      alt="Full Body PPF Protection Coverage"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Full Body Protection
                    </h4>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood, Roof, and Trunk</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders and Quarters</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front and Rear Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Doors and Rocker Panels</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps & Pillars</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 italic text-center mt-6">
                      Covers all painted areas of your vehicle
                    </p>
                  </CardContent>
                </Card>

                {/* Track Pack Coverage */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariTrackPack}
                      alt="Track Pack PPF Coverage"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Track Pack Coverage
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Rocker Panels</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">A Pillar and Roofstrip</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Full Front Protection */}
                <Card className="bg-[#2A2A2A] border-white/10 rounded-2xl overflow-hidden">
                  <div className="relative h-64 overflow-hidden bg-black rounded-t-2xl">
                    <img 
                      src={ferrariFullFront}
                      alt="Full Front PPF Protection Coverage"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h4 className="text-2xl font-heading font-bold text-white mb-4">
                      Full Front Protection
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Hood</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Fenders</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Full Front Bumper</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Mirror Caps</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">Headlights</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Motorcycle PPF Section */}
      <section className="py-20 bg-darker-bg border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Bike className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Motorcycle Paint Protection Film
              </h2>
              <p className="text-xl text-muted-foreground">
                Premium protection specifically designed for motorcycles, sport bikes, and touring bikes
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-accent" />
                    Common Coverage Areas
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Gas Tank (Full Coverage)</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Front Fender & Side Fairings</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Swingarm & Frame Guards</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Tail Section & Rear Fender</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Custom High-Impact Zones</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Motorcycle-Specific Benefits
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Protection from chain lube splatter</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Guards against boot rub and scratches</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Rock chip defense for exposed surfaces</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Maintains resale value of rare bikes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">Custom-cut patterns for any model</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-muted-foreground mb-6">
                Custom PPF solutions for sport bikes, cruisers, adventure bikes, and touring motorcycles
              </p>
              <Button asChild variant="premium" size="lg">
                <Link to="/contact">Request Motorcycle Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Options Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Palette className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                PPF Customization Options
              </h2>
              <p className="text-xl text-muted-foreground">
                Transform your vehicle's appearance while protecting the paint
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Stealth/Matte PPF */}
              <div>
                <Card className="bg-card border-border h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold">XPEL Stealth (Matte) PPF</h3>
                        <p className="text-sm text-accent">Gloss to Satin Finish Conversion</p>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-6">
                      Transform your glossy paint into a stunning satin/matte finish while protecting it. XPEL Stealth film provides the same self-healing protection as Ultimate Plus, with a unique matte appearance.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Reversible matte finish (no paint modification)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Self-healing matte surface technology</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Available on any package (partial to full body)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Distinctive luxury appearance</span>
                      </div>
                    </div>

                    {/* XPEL Stealth Video */}
                    <div className="relative aspect-video rounded-lg overflow-hidden border border-border mb-4">
                      <video
                        className="w-full h-full object-cover"
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                      >
                        <source src="/videos/xpel-stealth-demo.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Colored PPF */}
              <div>
                <Card className="bg-card border-border h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                        <Palette className="w-8 h-8 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading font-bold">XPEL Colored PPF</h3>
                        <p className="text-sm text-accent">Custom Color + Protection</p>
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-6">
                      Change your vehicle's color while adding protection. XPEL offers a range of vibrant, high-quality colored PPF options that combine paint protection with a complete color transformation.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Full color change without permanent paint</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Self-healing colored film technology</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Wide range of colors and finishes</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">Protects original factory paint underneath</span>
                      </div>
                    </div>

                    {/* XPEL Colored PPF Video */}
                    <div className="relative aspect-video rounded-lg overflow-hidden border border-border mb-4">
                      <iframe
                        src="https://www.youtube.com/embed/Ssw4bl9AqK8"
                        title="XPEL Colored PPF"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-6">
                Interested in Stealth or Colored PPF? Contact us to see samples and discuss your vision.
              </p>
              <Button asChild variant="premium" size="lg">
                <Link to="/contact">Explore Customization Options</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Protect Your Investment Today
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Schedule a consultation to discuss the best protection package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Paint Protection Film",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films"
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional XPEL paint protection film installation providing self-healing technology, rock chip protection, and long-term paint preservation."
        })}
      </script>
    </div>
  );
};

export default PaintProtectionFilm;
